// Reading a vendor's pack description — one implementation, shared.
//
// Two modules ask a pack string what it means: purchaseConversion.mjs (how
// many COUNT UNITS is one package?) and priceComparison.mjs (how much of the
// relationship's comparison unit is in one package?). They used to parse it
// separately, and separately means differently: "2 sacks × 25 lb" measured 50
// lb in the comparison and was unresolvable in the conversion, and
// "4 bags × 12 bunches" counted 48 bunches in one and fell back to a
// case_quantity of 4 in the other. Both questions start from the same reading,
// so the reading lives here.
//
// The arithmetic engine is still the Menu Recipe Costing Lab's units.mjs
// (canonical bases g/ml/each) — this module only prepares strings for it and
// classifies the words it deliberately leaves alone. Pure ESM, no Deno APIs.
//
// Nothing here guesses a SIZE. It reads what the vendor wrote: a wrapper is a
// wrapper, a stated count is a count, and a package whose contents are not
// stated comes back unread — for the AI bridge to estimate, never for this
// module to invent.

import { normalizeUnit, parsePackSize } from "../menu-recipe-costing/units.mjs";

// ── Words ────────────────────────────────────────────────────────────────────

// Abbreviations suppliers write instead of the word.
const ABBREVIATIONS = { cs: "case", bx: "box", btl: "bottle", pk: "pack", ea: "each", bg: "bag", crtn: "carton", ctn: "carton" };

/** Singular, abbreviation-expanded pack word ("CS" / "bags" → "case" / "bag"). */
export function canonicalPackWord(word) {
  let w = String(word ?? "").trim().toLowerCase().replace(/\.+$/, "");
  if (!w) return null;
  // "bag in box" / "bag-in-box" / "BIB" are one container, however written.
  if (/^bags?[ -]?in[ -]?box(?:es)?$/.test(w)) return "bib";
  w = ABBREVIATIONS[w] ?? w;
  if (w === "bib" || w === "bibs") return "bib";
  if (w.endsWith("ves")) return `${w.slice(0, -3)}f`; // loaves → loaf
  if (/(ch|sh|x|ss)es$/.test(w)) return w.slice(0, -2); // pouches → pouch, boxes → box
  if (w.endsWith("s") && !w.endsWith("ss")) return w.slice(0, -1);
  return w;
}

// Container words the Recipe Lab engine does not classify (its list is
// conservative on purpose). These carry no size, they just wrap one.
export const EXTRA_CONTAINER_WORDS = new Set([
  "pouch", "pouches", "keg", "kegs", "packet", "packets", "sachet", "sachets",
  "brick", "bricks", "block", "blocks", "crate", "crates", "drum", "drums",
  "bin", "bins", "tube", "tubes", "canister", "canisters", "cannister",
  "cannisters", "wheel", "wheels", "stick", "sticks", "bib", "bibs",
]);

/** Any word that wraps a quantity rather than being one. */
export function isContainerWord(word) {
  if (normalizeUnit(word).kind === "container") return true;
  return EXTRA_CONTAINER_WORDS.has(String(word ?? "").trim().toLowerCase());
}

// One PIECE of a product. The engine files "head" and "bunch" under container
// words — it is conservative on purpose and has no reason to tell a piece from
// a wrapper — so the distinction is drawn here.
export const PIECE_WORDS = new Set([
  "head", "heads", "bunch", "bunches", "heart", "hearts", "ear", "ears",
  "stalk", "stalks", "bulb", "bulbs", "clove", "cloves", "sprig", "sprigs",
  "loaf", "loaves", "roll", "rolls", "link", "links", "fillet", "fillets",
  "filet", "filets", "patty", "patties", "breast", "breasts", "thigh",
  "thighs", "wheel", "wheels", "stick", "sticks", "log", "logs", "round",
  "rounds", "portion", "portions", "slice", "slices", "sheet", "sheets",
]);

// SHIPPING wrappers — whatever follows the number, these are never a piece
// count, because the wrapper says nothing about how much is inside.
export const SHIPPING_CONTAINERS = new Set([
  "case", "cases", "cs", "box", "boxes", "bx", "bag", "bags", "bg", "sack", "sacks",
  "carton", "cartons", "crate", "crates", "pallet", "pallets", "pail", "pails",
  "tote", "totes", "tray", "trays", "flat", "flats", "sleeve", "sleeves",
  "pack", "packs", "pk", "package", "packages", "container", "containers",
  "tub", "tubs", "jar", "jars", "can", "cans", "bottle", "bottles", "btl",
  "jug", "jugs", "tin", "tins", "drum", "drums", "bin", "bins", "keg", "kegs",
  "pouch", "pouches", "packet", "packets", "sachet", "sachets", "canister",
  "canisters", "cannister", "cannisters", "bib", "bibs",
]);

/** True for a wrapper whose contents are unstated — a bag, a case, a pail. */
export function isShippingContainer(word) {
  return SHIPPING_CONTAINERS.has(String(word ?? "").trim().toLowerCase().replace(/\.+$/, ""));
}

/**
 * What kind of thing an item's own COUNT UNIT is, for the resolvers that turn
 * a typed quantity into a stored one (parAndMoq.normalizePar,
 * countEntryMath.resolveCountEntry).
 *
 * The Recipe Lab engine answers "can I convert this?" and files every wrapper
 * word under `container` with no base and no factor. Read as "is this a unit
 * at all?" that answer is wrong: a bar counts BOTTLES, a produce kitchen
 * counts CASES, and "3 bottles" on a bottle-counted item is three bottles by
 * identity — no conversion required, none invented. Refusing those units
 * outright is what made every count entry AND the PAR unresolvable on a
 * bottle-counted item while the same row in `each` worked perfectly.
 *
 * So an anchorable count unit is one of two things, and the difference decides
 * what may resolve against it:
 *
 *   `measure`   — mass/volume/count, with a base. Converts freely
 *                 ("2.5 gal" into a fl-oz item).
 *   `container` — a wrapper or piece noun (bottle, case, bag, bib, head).
 *                 Resolves ONLY by identity (bottle → bottle) or through a
 *                 named vendor package whose ladder rung states how many
 *                 count units one package holds. It never acquires a base, so
 *                 a cross-dimension conversion into it stays refused — the
 *                 same "container identity is order-safe, arithmetic across
 *                 dimensions is not" rule packageBasis already applies.
 *
 * `null` for anything nameless: '', a bare number, an unknown word.
 *
 * @returns {{ anchorable:boolean, kind:'measure'|'container'|null, key:string|null }}
 */
export function countUnitAnchor(unitRaw) {
  const raw = String(unitRaw ?? "").trim();
  if (!raw) return { anchorable: false, kind: null, key: null };

  const norm = normalizeUnit(raw);
  if (norm.kind === "mass" || norm.kind === "volume" || norm.kind === "count") {
    return { anchorable: true, kind: "measure", key: norm.key };
  }

  // A wrapper the engine classified, a wrapper only this module knows
  // ("bib"), or a piece noun ("head", "patty"). All three are real shelves to
  // count on; none of them is an amount.
  const word = canonicalPackWord(raw);
  if (
    norm.kind === "container"
    || (word && (EXTRA_CONTAINER_WORDS.has(word) || SHIPPING_CONTAINERS.has(word) || PIECE_WORDS.has(word)))
  ) {
    return { anchorable: true, kind: "container", key: norm.key || word };
  }

  return { anchorable: false, kind: null, key: null };
}

/**
 * Do two count-unit names refer to the same shelf? Identity only — never a
 * conversion — so it is safe for `container` units, which have no base.
 */
export function sameCountUnitName(a, b) {
  const left = countUnitAnchor(a);
  const right = countUnitAnchor(b);
  if (!left.anchorable || !right.anchorable) return false;
  if (left.key && right.key && left.key === right.key) return true;
  const wordA = canonicalPackWord(a);
  const wordB = canonicalPackWord(b);
  return !!wordA && wordA === wordB;
}

export const PIECE_WORD_CONFIDENCE = 0.9;
export const PLURAL_NOUN_CONFIDENCE = 0.8;

/**
 * The piece noun a pack's unit phrase names, singularized — or null when the
 * phrase describes a wrapper, a real measure, or nothing countable.
 *
 * Accepted: a known piece word ("24 heads"), or a plural noun that is neither
 * a wrapper nor a unit the engine already understands ("88 apples" — a vendor
 * writing a bare plural after a number is stating how many are in the box).
 */
export function readPieceNoun(unitRaw) {
  const tokens = String(unitRaw ?? "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  const last = tokens[tokens.length - 1]?.replace(/[.,]+$/, "");
  if (!last || !/^[a-z]+$/.test(last)) return null;
  if (isShippingContainer(last)) return null;
  if (PIECE_WORDS.has(last)) {
    return { word: canonicalPackWord(last), confidence: PIECE_WORD_CONFIDENCE };
  }
  // Anything the engine can already classify is not an unknown product noun.
  if (normalizeUnit(last).kind !== "unknown") return null;
  if (last.length >= 4 && /(?:[^s]s|es)$/.test(last)) {
    return { word: canonicalPackWord(last), confidence: PLURAL_NOUN_CONFIDENCE };
  }
  return null;
}

// ── Text preparation ─────────────────────────────────────────────────────────

const APPROX_PREFIX = /^(?:approx(?:imately)?\.?|about|~|roughly)\s+/i;
const TRAILING_TOTAL = /\s+total$/i;

// "… / case", "… per bag" — a trailing wrapper the contents are packed in.
const TRAILING_PER_CONTAINER = /\s*(?:\/|\bper\b)\s*([a-zA-Z]+\.?)\s*$/i;

// Some product titles state the size of ONE inner portion before the number
// of those portions in the outer package: "1 Pound -- 12 Per Case". The
// Recipe Lab parser expects count first ("12 x 1 Pound"), so canonicalize
// only when both numeric facts, a real measurement unit, and a real outer
// wrapper are explicit. "1 Pound Per Case" deliberately does not match.
const CONTENTS_SIZE_COUNT_PER_CONTAINER = /(\d+(?:[.,]\d+)?)\s*([a-zA-Z]+(?:\s+[a-zA-Z]+)?)\s*(?:-{1,2}|[–—:;,])\s*(\d+(?:[.,]\d+)?)\s+(?:units?\s+)?per\s+([a-zA-Z]+\.?)/i;
// Food-service descriptions also put a metric equivalent and the word
// "each" between the inner size and case count:
// "10 lb. (4.54 kg) each; 2 packs per case". Both sizes describe ONE inner
// pack; only the explicit "2 packs per case" is the multiplier. Keep this
// narrower than general prose so MOQ text such as "2 case minimum" cannot
// become package contents.
const CONTENTS_SIZE_EACH_PACKS_PER_CONTAINER = /(\d+(?:[.,]\d+)?)\s*([a-zA-Z]+(?:\s+[a-zA-Z]+)?)\.?\s*(?:\((\d+(?:[.,]\d+)?)\s*([a-zA-Z]+(?:\s+[a-zA-Z]+)?)\.?\)\s*)?each\s*(?:-{1,2}|[–—:;,])\s*(\d+(?:[.,]\d+)?)\s+([a-zA-Z]+)\s+per\s+([a-zA-Z]+\.?)/i;
const COMPACT_POUND_CONTAINER = /(\d+(?:[.,]\d+)?)#([a-zA-Z]+)\b/i;

function equivalentMeasureMatches(quantity, unit, equivalentQuantity, equivalentUnit) {
  if (equivalentQuantity == null || equivalentUnit == null) return true;
  const primary = parsePackSize(`${quantity} ${unit}`);
  const equivalent = parsePackSize(`${equivalentQuantity} ${equivalentUnit}`);
  if (!primary.parsed || primary.special || !equivalent.parsed || equivalent.special
    || primary.unitNorm?.kind !== equivalent.unitNorm?.kind) return false;
  const primaryBase = primary.packCount * primary.unitQty * primary.unitNorm.factor;
  const equivalentBase = equivalent.packCount * equivalent.unitQty * equivalent.unitNorm.factor;
  if (!(primaryBase > 0 && equivalentBase > 0)) return false;
  return Math.abs(primaryBase - equivalentBase) / Math.max(primaryBase, equivalentBase) <= 0.03;
}

/**
 * Prepare a pack description for the Recipe Lab parser and remember the
 * container word it names.
 *
 * Supplier strings read "2 bags × 5 lb" / "12 pouches × 12 oz" — a count of
 * containers, a separator, then the size of one container. The parser expects
 * the separator directly after the leading number, so the container word is
 * lifted out (and remembered — identical containers can become the comparison
 * unit, and a container can be the count unit).
 *
 * The same fact is also written contents-first: "24 buns / case",
 * "12 rolls per bag", "16 oz / bottle". The trailing wrapper phrase is
 * lifted the same way, leaving the contents for the parser.
 *
 * Order guides write it wrapper-first as a bare abbreviation: "CS 6/#10",
 * "BG 50 LB", "BIB 5 GAL". The leading token names the ONE package the rest
 * of the string fills — the singular-wrapper meaning "12 x 32 oz case"
 * already has — so it lifts as a 'per' wrapper.
 *
 * `containerPosition` records WHERE the word stood, because position carries
 * meaning: a "per"-wrapper ("24 buns / case") is the package the contents sit
 * in, while a leading ("2 bags × 5 lb") or trailing ("24 × 6.8 oz bottles")
 * word names the counted containers themselves.
 *
 * @returns {{ text: string, containerWord: string|null,
 *             containerPosition: 'per'|'leading'|'trailing'|null }}
 */
export function liftLeadingContainer(raw) {
  let text = String(raw ?? "").trim();
  let containerWord = null;
  let containerPosition = null;
  if (!text) return { text, containerWord, containerPosition };

  text = text.replace(APPROX_PREFIX, "").replace(TRAILING_TOTAL, "");

  // Written decimal sizes and the supplier gallon abbreviation: ".5 ga".
  // This only canonicalizes tokens; the complete package still has to parse.
  text = text.replace(/(^|[\s(/x×])\.(\d+)/gi, (_, prefix, digits) => `${prefix}0.${digits}`)
    .replace(/(\d)\s*ga\b/gi, '$1 gal');
  // A terminal percentage is a product specification (e.g. butterfat), not
  // another package dimension. Preserve the original wording on the option.
  // "36%" alone and "36% off" still cannot establish package contents.
  text = text.replace(/\s+\d+(?:\.\d+)?\s*%\s*$/, '');

  // Catalogs wrap an explicit size in a selling-option label: "Unit (50 lb)"
  // or "Case (12 x 1 qt)". Unwrap only a complete, flat label whose contents
  // the existing engine already reads as mass/volume. Never strip arbitrary
  // parentheses, trailing sale counts, or alternative sizes. "Unit" is a
  // listing label, not evidence of one onion/egg; real wrappers keep their
  // existing single-package semantics below.
  const labeledSize = text.match(/^([a-zA-Z]+\.?)\s*\(([^()]*)\)$/);
  if (labeledSize) {
    const word = canonicalPackWord(labeledSize[1]);
    const genericUnit = /^unit\.?$/i.test(labeledSize[1]);
    const wrapper = word && !PIECE_WORDS.has(word)
      && (isContainerWord(word) || isShippingContainer(word));
    if (genericUnit || wrapper) {
      const contents = labeledSize[2].trim();
      const parsed = parsePackSize(contents);
      // The recipe parser is deliberately best-effort about trailing prose.
      // This new catalog shape is stricter: its entire trailing unit phrase
      // must itself be a unit, not "lb or more" / "qt or gallon" / "lb two
      // bags" reduced to a familiar prefix by that parser.
      const writtenUnit = normalizeUnit(contents.match(/[a-zA-Z#][a-zA-Z .#-]*$/)?.[0]);
      if (parsed.parsed && !parsed.special
        && (writtenUnit.kind === "mass" || writtenUnit.kind === "volume")
        && writtenUnit.key === parsed.unitNorm?.key) {
        text = genericUnit ? contents : `${labeledSize[1]} ${contents}`;
      }
    }
  }

  // WooCommerce titles commonly compact the U.S. pound marker against its
  // wrapper ("5#Bag"). Insert only the missing token boundary when the word
  // after # is a real wrapper. Can-size notation ("6/#10") has a number
  // after # and is therefore untouched and remains deliberately unresolved.
  const compactPound = text.match(COMPACT_POUND_CONTAINER);
  if (compactPound && isContainerWord(compactPound[2])) {
    text = text.replace(compactPound[0], `${compactPound[1]}# ${compactPound[2]}`);
  }

  const eachPacks = text.match(CONTENTS_SIZE_EACH_PACKS_PER_CONTAINER);
  if (eachPacks) {
    const unit = eachPacks[2].trim().replace(/\.+$/, "");
    const unitKind = normalizeUnit(unit).kind;
    const innerWrapper = eachPacks[6].replace(/\.+$/, "");
    const outerWrapper = eachPacks[7].replace(/\.+$/, "");
    if ((unitKind === "mass" || unitKind === "volume" || unitKind === "count")
      && isContainerWord(innerWrapper) && isContainerWord(outerWrapper)
      && equivalentMeasureMatches(eachPacks[1], unit, eachPacks[3], eachPacks[4])) {
      containerWord = canonicalPackWord(outerWrapper);
      containerPosition = "per";
      text = `${eachPacks[5]} × ${eachPacks[1]} ${unit}`;
    }
  }

  const contentsFirst = containerWord == null ? text.match(CONTENTS_SIZE_COUNT_PER_CONTAINER) : null;
  if (contentsFirst) {
    const unit = contentsFirst[2].trim();
    const unitKind = normalizeUnit(unit).kind;
    const wrapper = contentsFirst[4].replace(/\.+$/, "");
    if ((unitKind === "mass" || unitKind === "volume" || unitKind === "count")
      && isContainerWord(wrapper)) {
      containerWord = canonicalPackWord(wrapper);
      containerPosition = "per";
      text = `${contentsFirst[3]} × ${contentsFirst[1]} ${unit}`;
    }
  }

  // "CS 6/#10" / "BG 50 LB" → the bare leading wrapper is the single package
  // the remaining contents fill, so it carries the 'per' position (never a
  // counted container). Only a genuine wrapper word before numbered contents
  // is read this way — a piece noun ("SLICE 6/1.5 LB") describes what is
  // inside and is left alone, and a wrapper with no numbered contents after
  // it ("CASE LOT") stays the subjective-container shape it always was.
  const bareLeading = text.match(/^([a-zA-Z]+\.?)\s+(.+)$/);
  if (bareLeading && /\d/.test(bareLeading[2])) {
    const word = canonicalPackWord(bareLeading[1]);
    if (word && !PIECE_WORDS.has(word)
      && (isContainerWord(word) || isShippingContainer(word))) {
      containerWord = word;
      containerPosition = "per";
      text = bareLeading[2].trim();
    }
  }

  // "24 buns / case" → "24 buns", container 'case'. Only a real wrapper word
  // is lifted ("24/16 oz" keeps its ratio), and only when a measurement
  // remains — "per case" alone describes nothing.
  const per = containerWord == null ? text.match(TRAILING_PER_CONTAINER) : null;
  if (per && isContainerWord(per[1].replace(/\.+$/, ""))) {
    const contents = text.slice(0, per.index).trim();
    if (contents && /\d/.test(contents)) {
      containerWord = canonicalPackWord(per[1]);
      containerPosition = "per";
      text = contents;
    }
  }

  // "2 bags × 5 lb" → "2 × 5 lb", container 'bag'. When a trailing wrapper
  // was already lifted ("4 packs × 12 buns / case") the OUTER word keeps
  // naming the package; the inner word is still lifted out of the text.
  const leading = text.match(/^(\d+(?:[.,]\d+)?)\s+([a-zA-Z]+)\s*([-/xX×])\s*/);
  if (leading && isContainerWord(leading[2])) {
    if (containerWord == null) {
      containerWord = canonicalPackWord(leading[2]);
      containerPosition = "leading";
    }
    text = `${leading[1]} ${leading[3]} ${text.slice(leading[0].length)}`;
  }

  // Trailing container word(s). NUMBER matters: a PLURAL trailing noun names
  // the counted pieces ("24 × 12 fl oz cans" — 24 cans), while a SINGULAR one
  // names the single wrapper the contents sit in ("12 x 32 oz case" — twelve
  // 32-oz units in ONE case, exactly like "… / case"). Reading the singular
  // wrapper as 12 counted cases is how a case-for-case identity once got
  // refused as a divided-price conflict.
  if (!containerWord) {
    const tokens = text.split(/\s+/);
    for (let i = tokens.length - 1; i > 0; i -= 1) {
      const bare = tokens[i].replace(/[.,]+$/, "");
      if (isContainerWord(bare)) {
        containerWord = canonicalPackWord(bare);
        const lower = bare.toLowerCase().replace(/\.+$/, "");
        const plural = containerWord != null && lower !== containerWord
          && (lower === `${containerWord}s`
            || lower === `${containerWord}es`
            || (containerWord.endsWith("f") && lower === `${containerWord.slice(0, -1)}ves`));
        containerPosition = plural ? "trailing" : "per";
        break;
      }
      // stop at the first non-container tail token
      break;
    }
  }

  return { text, containerWord, containerPosition };
}


// ── Fallback readings for wordings the engine's grammar does not cover ───────
//
// The engine reads count-first ("24 x 355 ml"). Invoices and product pages
// also write the size first ("355ml x 24", "12oz Bottle x 24") or list the
// facts as separate clauses ("330 mL, 24 ct"; "12 fl oz (355 mL) bottle;
// 24 ct; classic look of the glass bottles…"; "Medium, 45lb Carton"; "50 lb
// Crtn, 100 cnt, US #1"). The 2026-09-06 HiHo report lost every beverage
// line to the first shape and every produce carton to the third. These
// readings run ONLY after the engine has failed to parse the string, so no
// wording the engine already read can change meaning; they rewrite the text
// into the count-first form the engine reads and hand it straight back.
//
// A count clause multiplies the size only when the size describes ONE PIECE:
// a unit container is named ("12 fl oz bottle; 24 ct") or the size is a
// volume ("330 mL, 24 ct" — a case of 24 bottles, never 330 mL split 24
// ways).
//
// A weight with an OUTER wrapper beside a PIECE count ("50 lb Crtn, 100 cnt",
// "50# CTN 70CT", "1/40 LB 140 CT") is the produce carton: the carton's
// weight and roughly how many pieces are inside — the same fact the engine's
// slash form "40 LB / 140 CT" has always read, and the dual-measure shape
// (weight stays the primary measurement, the count becomes pieceCount, never
// a multiplier). Produce invoices print exactly this ("POTATO RUSSET 50# CTN
// 70CT"; a grade such as "US #1" says nothing about size and is ignored).
// DO NOT remove this reading to "fix" a case where the count turns out to be
// a count of BAGS: that case is written with a unit-of-sale word, and the
// rules below keep it unread on purpose.
//
// Refused on purpose, and the reasons — a wrong-but-confident package
// quantity becomes a purchase order, an unread one becomes a repair card:
//   - a weight with an outer wrapper beside a UNIT-OF-SALE count ("50 LB
//     BAG, 2 EA", "5 lb bag, 2 pcs") stays unread: "2 EA" more often means
//     two bags (100 lb) than two pieces in one bag, and the text cannot say
//     which. "ea/each/pc/pcs" beside a wrapper is a sale unit; "ct/cnt/count"
//     is a piece count. Do not merge the two word lists.
//   - a bare weight beside any count ("10 lb, 4 ct") stays unread: four 10 lb
//     packs or a 10 lb pack of four, and nothing in the text decides.
//   - a volume beside a count with NO separator ("1 GAL 4 CT") stays unread:
//     the clause form multiplies a volume ("1 gal, 4 ct" is four gallons) and
//     the produce form counts pieces, and a separator-less string carries
//     neither signal.

// Piece counts: what is inside one package ("140 CT" lemons).
const PIECE_COUNT_CLAUSE_RE = /^(\d+)\s*(?:ct|cnt|count)\.?$/i;
// Units of sale: how many packages ("2 EA" bags) — never a piece count.
const SALE_COUNT_CLAUSE_RE = /^(\d+)\s*(?:pcs?|pieces?|ea|each)\.?$/i;
// "50# CTN 70CT", "1/40 LB 140 CT", "40 LB 140CT": a weight, then a piece count.
const WEIGHT_THEN_COUNT_RE = /^(.*\S)\s+(\d+)\s*(?:ct|cnt|count)\.?$/i;
const GRADE_CLAUSE_RE = /^(?:us|usda)\s*#?\s*\d+[a-z]?$/i;
const SIZE_FIRST_COUNT_RE = /^(.*?\S)\s*[xX×*]\s*(\d+)\s*([a-zA-Z]+\.?)?$/;
const SIZE_PHRASE_RE = /^(\d+(?:[.,]\d+)?)\s*([a-zA-Z][a-zA-Z .]*)$/;
// Words a product page puts between a size and its container that describe
// the container's material, never its contents: "32 fl oz plastic bottle".
const MATERIAL_WORDS = new Set(["plastic", "glass", "aluminum", "aluminium", "metal", "paper", "foil", "tin", "pet"]);
const OUTER_WRAPPERS = new Set([
  "case", "carton", "crate", "sack", "bag", "box", "pallet", "tote", "tray", "flat",
  "sleeve", "pail", "tub", "bin",
]);

/**
 * One size clause — "355ml", "12 fl oz (355 mL) bottle", "45lb Carton",
 * "50 lb Crtn" — as a quantity, a real mass/volume unit, and the container
 * word that followed it, or null. A parenthetical metric/US equivalent must
 * agree with the primary figure or the clause is refused.
 */
function readSizeClause(raw) {
  let text = String(raw ?? "").trim();
  let equivalent = null;
  const paren = text.match(/\(([^()]*)\)/);
  if (paren) {
    equivalent = paren[1].trim();
    text = `${text.slice(0, paren.index)} ${text.slice(paren.index + paren[0].length)}`
      .replace(/\s+/g, " ").trim();
  }
  const m = text.match(SIZE_PHRASE_RE);
  if (!m) return null;
  const qty = Number(m[1].replace(",", "."));
  if (!(qty > 0)) return null;
  const tokens = m[2].trim().split(/\s+/);
  for (let end = tokens.length; end >= 1; end -= 1) {
    const phrase = tokens.slice(0, end).join(" ").replace(/\.+$/, "");
    const norm = normalizeUnit(phrase);
    if (norm.kind !== "mass" && norm.kind !== "volume") continue;
    const dropped = tokens.slice(end).map((t) => canonicalPackWord(t.replace(/[.,]+$/, "")))
      .filter((w) => !MATERIAL_WORDS.has(w));
    if (dropped.some((w) => !w || !(isContainerWord(w) || isShippingContainer(w)))) return null;
    if (equivalent) {
      const e = equivalent.match(SIZE_PHRASE_RE);
      if (!e || !equivalentMeasureMatches(qty, phrase, Number(e[1].replace(",", ".")), e[2].trim())) return null;
    }
    return { qty, unit: phrase, kind: norm.kind, containerWord: dropped.length ? dropped[dropped.length - 1] : null };
  }
  return null;
}

/** "355ml x 24" / "12oz Bottle x 24" → "24 × 355 ml" counting 24 bottles. */
function readSizeFirstCount(text) {
  const m = text.match(SIZE_FIRST_COUNT_RE);
  if (!m) return null;
  const count = Number(m[2]);
  if (!Number.isInteger(count) || count < 1) return null;
  const tail = m[3] ? m[3].replace(/\.+$/, "") : null;
  if (tail && !/^(?:ct|cnt|count|pk|pack|pcs?|pieces?|ea|each)$/i.test(tail)) return null;
  const size = readSizeClause(m[1]);
  if (!size) return null;
  return {
    text: `${count} × ${size.qty} ${size.unit}`,
    containerWord: size.containerWord,
    containerPosition: size.containerWord ? "trailing" : null,
  };
}

function splitClauses(text) {
  const clauses = [];
  let depth = 0;
  let current = "";
  for (const ch of String(text)) {
    if (ch === "(") depth += 1;
    if (ch === ")") depth = Math.max(0, depth - 1);
    if ((ch === ";" || ch === ",") && depth === 0) { clauses.push(current); current = ""; continue; }
    current += ch;
  }
  clauses.push(current);
  return clauses.map((c) => c.trim()).filter(Boolean);
}

/**
 * Comma/semicolon clauses: one size clause, at most one count clause, plus
 * descriptors ("Medium", "Burbank"), grades ("US #1") and prose the reading
 * ignores. Any other clause carrying a digit means the string is not
 * understood, and it stays unread.
 */
function readClauses(text, priorContainer) {
  const clauses = splitClauses(text);
  if (clauses.length < 2) return null;
  let size = null;
  let count = null;
  let countIsSaleUnit = false;
  let container = priorContainer ?? null;
  for (const clause of clauses) {
    if (!/\d/.test(clause)) {
      const word = canonicalPackWord(clause);
      if (word && !container && (isContainerWord(word) || isShippingContainer(word))) container = word;
      continue;
    }
    if (GRADE_CLAUSE_RE.test(clause)) continue;
    const counted = clause.match(PIECE_COUNT_CLAUSE_RE) ?? clause.match(SALE_COUNT_CLAUSE_RE);
    if (counted) {
      if (count != null) return null;
      count = Number(counted[1]);
      countIsSaleUnit = !PIECE_COUNT_CLAUSE_RE.test(clause);
      continue;
    }
    const read = readSizeClause(clause);
    if (!read || size) return null;
    size = read;
    if (read.containerWord) container = read.containerWord;
  }
  if (!size || !(count == null || count >= 1)) return null;
  if (count == null) {
    return { text: `${size.qty} ${size.unit}`, containerWord: container, containerPosition: container ? "per" : null };
  }
  const outer = container != null && OUTER_WRAPPERS.has(container);
  if (size.kind === "volume" || (container != null && !outer)) {
    return {
      text: `${count} × ${size.qty} ${size.unit}`,
      containerWord: container,
      containerPosition: container ? "trailing" : null,
    };
  }
  // The produce carton: weight of the wrapper, PIECE count of what is inside.
  // A unit-of-sale count beside a wrapper is refused (see the notes above).
  if (outer && size.kind === "mass" && !countIsSaleUnit) {
    return { text: `${size.qty} ${size.unit} / ${count} ct`, containerWord: container, containerPosition: "per" };
  }
  return null;
}

/**
 * "50# CTN 70CT" / "1/40 LB 140 CT" / "40 LB 140CT" — the produce line as
 * broadline software prints it, with no separator between the carton weight
 * and the piece count. Mass only (see the notes above); the weight side is
 * read by the engine's own grammar so "1/40 LB" keeps meaning one 40 lb pack.
 */
function readWeightThenCount(text, priorContainer) {
  const m = text.match(WEIGHT_THEN_COUNT_RE);
  if (!m) return null;
  const count = Number(m[2]);
  if (!Number.isInteger(count) || count < 1) return null;
  const lifted = liftLeadingContainer(m[1]);
  const weight = parsePackSize(lifted.text);
  if (!weight.parsed || weight.special || weight.unitNorm?.kind !== "mass") return null;
  const total = weight.packCount * weight.unitQty;
  if (!(total > 0)) return null;
  const container = lifted.containerWord ?? priorContainer ?? null;
  return { text: `${total} ${weight.unitRaw} / ${count} ct`, containerWord: container, containerPosition: container ? "per" : null };
}

/**
 * The engine-readable rewrite of a string the engine could not parse, or
 * null. Narrow source wordings only; never a guess at a size.
 */
export function readUnstructuredPackText(text, priorContainer = null) {
  // Catalog titles sometimes name a carton followed by its counted inner
  // containers and their individual size: "cartons 20 1-lb plastic jars".
  // Require the inner container noun and an actual mass unit. A bare weight
  // plus an unrelated count remains the deliberately unread produce boundary.
  if (priorContainer === 'carton') {
    const inner = text.match(/^(\d+)\s+(\d+(?:\.\d+)?)\s*-\s*(lb|lbs|oz|kg|g)\s+(?:(?:plastic|film|glass|paper)\s+)?(jars?|bags?)(?:\s*\/\s*(?:(?:plastic|film|glass|paper)\s+)?(?:jars?|bags?))?$/i);
    if (inner && Number(inner[1]) > 0 && Number(inner[2]) > 0) {
      return { text: `${inner[1]} × ${inner[2]} ${inner[3]}`,
        containerWord: 'carton', containerPosition: 'per' };
    }
  }
  // The slash in "2 Liter - 6/Case" was lifted as an outer wrapper; the
  // remaining size-then-count is an explicit volume count, not a density or
  // contents estimate. Do not apply this to a bare weight-plus-count.
  if (priorContainer && isShippingContainer(priorContainer)) {
    const volume = text.match(/^(\d+(?:\.\d+)?)\s+(l|liter|liters|litre|litres|ml|milliliters?|fl\s*oz|fluid\s+ounces?)\s*[-–—]\s*(\d+)$/i);
    if (volume && Number(volume[1]) > 0 && Number(volume[3]) > 0) {
      return { text: `${volume[3]} × ${volume[1]} ${volume[2]}`,
        containerWord: priorContainer, containerPosition: 'per' };
    }
  }
  // USDA terminal rows print an explicit inner count and weight as
  // "master container 15 3-lb mesh sacks". This is exactly 15 × 3 lb;
  // a missing size or nonadjacent count is not enough to infer contents.
  const master = text.match(/^master container\s+(\d+)\s+(\d+(?:\.\d+)?)\s*[- ]\s*(lb|lbs|oz|kg|g)\b(?:\s+(?:mesh\s+)?(?:sacks?|bags?))?$/i);
  if (master && Number(master[1]) > 0 && Number(master[2]) > 0) {
    return { text: `${master[1]} × ${master[2]} ${master[3]}`,
      containerWord: priorContainer ?? 'container', containerPosition: 'per' };
  }
  return readSizeFirstCount(text) ?? readClauses(text, priorContainer)
    ?? readWeightThenCount(text, priorContainer) ?? readSingleSizeClause(text, priorContainer);
}

/** "32 fl oz (946 mL) plastic bottle" — one size with an equivalent and a container. */
function readSingleSizeClause(text, priorContainer) {
  if (!/\(/.test(text)) return null;
  const size = readSizeClause(text);
  if (!size) return null;
  const container = size.containerWord ?? priorContainer ?? null;
  return { text: `${size.qty} ${size.unit}`, containerWord: container, containerPosition: container ? "per" : null };
}

// ── The reading ──────────────────────────────────────────────────────────────

/**
 * Read one pack description into a measurement, or say it could not be read.
 *
 * The single entry point both callers use, so one string can never mean two
 * things. Reads, in order: the outer container word, the engine's own parse,
 * and — when the engine lands on a word it will not measure — whether that
 * word names a countable PIECE.
 *
 * One package can carry SEVERAL simultaneous physical facts. "16/12 OZ" is at
 * once sixteen pieces, twelve ounces per piece, and 192 ounces of pork; those
 * are complements, not rivals. The legacy single-measurement fields keep their
 * exact meaning, and the additional facts ride alongside:
 *   pieceCount   — explicit count of pieces/inner units in ONE package, in
 *                  eaches ("20/12 OZ" → 20; "15 doz" → 180; "88 apples" → 88).
 *                  Null when the vendor stated no count — an implied 1 of a
 *                  single measure ("50 LB") is NOT a piece count.
 *   perPiece     — { qty, unit, min, max, approximate } when the text sizes
 *                  one piece ("12 OZ", "3-3.5 LB", "3.25 LB AVG").
 *   measures     — every independent fact as
 *                  { kind, totalQty, unitToken, approximate, source }.
 *   approximate  — the primary measurement is nominal (range / AVG).
 *
 * @returns {{ read: boolean, reason: string|null,
 *             packCount: number|null, unitQty: number|null,
 *             totalQty: number|null, unitRaw: string|null,
 *             unitToken: string|null, unitNorm: object|null,
 *             kind: 'mass'|'volume'|'count'|null,
 *             containerWord: string|null, pieceWord: string|null,
 *             special: string|null, confidence: number, text: string,
 *             pieceCount: number|null, pieceCountSource: string|null,
 *             perPiece: object|null, measures: object[], approximate: boolean }}
 *   `unitToken` is a unit string the engine's convertToBase understands —
 *   the matched unit, or 'each' for a piece count. `totalQty` is
 *   packCount × unitQty in that unit. Unread packs carry `read: false` and a
 *   reason; nothing is ever guessed into a number.
 */
export function readPack(raw) {
  const { text, containerWord, containerPosition } = liftLeadingContainer(raw);
  const out = {
    read: false, reason: null,
    packCount: null, unitQty: null, totalQty: null,
    unitRaw: null, unitToken: null, unitNorm: null, kind: null,
    containerWord, containerPosition, pieceWord: null, special: null,
    confidence: 0, text,
    pieceCount: null, pieceCountSource: null, perPiece: null,
    measures: [], approximate: false,
  };
  if (!text) return { ...out, reason: "no_pack_description" };

  // The recipe grammar treats "12-14 oz" as twelve 14-oz packs. In a
  // product title this is a RANGE for one piece, never a measured package.
  // An independently printed case/ct count remains an exact count fact.
  // Inspect the original wording: liftLeadingContainer may otherwise treat
  // the upper end of a range as a size and erase the lower end.
  const original = String(raw ?? '').trim();
  const range = original.match(/\b(\d+(?:\.\d+)?)\s*[-–—]\s*(\d+(?:\.\d+)?)\s*(oz|lb|lbs|g|kg)\b/i);
  // The established "12/3-3.5 LB" form has an explicit leading package
  // count and already carries approximate midpoint semantics. Preserve it;
  // only an uncounted title range needs protection from the recipe grammar.
  const countedRangeForm = /^\d+\s*\/\s*\d+(?:\.\d+)?\s*[-–—]\s*\d+(?:\.\d+)?\s*[a-z]+\b/i;
  const countedRange = countedRangeForm.test(original) || countedRangeForm.test(text);
  if (range && !countedRange && Number(range[1]) < Number(range[2])) {
    const withoutRange = original.replace(range[0], ' ').trim();
    const countMatch = withoutRange.match(/^(\d+)\s*(?:ct|cnt|count)\b/i)
      ?? withoutRange.match(/\b(\d+)\s+(?:per\s+|\/\s*)(case|carton)\b/i);
    // A second numeric fact (MOQ, a second pack, another size) makes the
    // package ambiguous; do not turn the first count into an orderable pack.
    const count = countMatch && !/\d/.test(withoutRange.replace(countMatch[0], ' '))
      ? Number(countMatch[1]) : null;
    const perPiece = { qty: null, unit: range[3], min: Number(range[1]),
      max: Number(range[2]), approximate: true };
    if (Number.isInteger(count) && count > 0) {
      return { ...out, read: true, kind: 'count', totalQty: count, unitQty: count,
        packCount: 1, unitToken: 'each',
        unitNorm: { key: 'each', kind: 'count', factor: 1, base: 'each' },
        pieceCount: count, pieceCountSource: 'stated_count', perPiece,
        measures: [countMeasure(count, 'explicit_pack_text')],
        containerWord: countMatch[2] ? canonicalPackWord(countMatch[2]) : containerWord,
        containerPosition: countMatch[2] ? 'per' : containerPosition,
        confidence: 0.9 };
    }
    return { ...out, reason: 'ranged_piece_size', perPiece, approximate: true };
  }

  // ── Dual measure: "40 LB / 140 CT", "4 PC / AVG 60 LB" ─────────────────────
  // Two complete measures of DIFFERENT kinds joined by a slash — a stated
  // count of pieces AND the weight/volume they add up to. Both facts are kept;
  // the weight/volume side stays the primary measurement (existing consumers
  // compare prices by it), the count side becomes pieceCount.
  const dual = readDualMeasure(text);
  if (dual) return { ...out, ...dual };

  let pack = parsePackSize(text);
  if (!pack.parsed) {
    const rewritten = readUnstructuredPackText(text, containerWord);
    if (rewritten) {
      out.text = rewritten.text;
      out.containerWord = rewritten.containerWord;
      out.containerPosition = rewritten.containerPosition;
      const rewrittenDual = readDualMeasure(rewritten.text);
      if (rewrittenDual) return { ...out, ...rewrittenDual, reading: "rewritten" };
      pack = parsePackSize(rewritten.text);
      out.reading = "rewritten";
    }
  }
  out.confidence = pack.confidence ?? 0;
  out.packCount = pack.packCount;
  out.unitQty = pack.unitQty;
  out.unitRaw = pack.unitRaw;
  out.unitNorm = pack.unitNorm;
  out.special = pack.special;
  out.approximate = pack.approximate === true;
  if (!pack.parsed) return { ...out, reason: "unparseable_pack" };
  if (pack.special === "number_can") {
    // "6/#10" — the can's contents are unstated, but the vendor did state a
    // count of six cans. The container count is a real fact even though the
    // size is not.
    if (pack.packCountExplicit && pack.packCount > 0) {
      out.pieceCount = pack.packCount;
      out.pieceCountSource = "container_count";
      out.measures = [countMeasure(pack.packCount, "explicit_pack_text")];
    }
    return { ...out, reason: "subjective_container" };
  }
  if (pack.special) return { ...out, reason: "subjective_container" };

  const kind = pack.unitNorm?.kind;
  if (kind === "mass" || kind === "volume" || kind === "count") {
    const totalQty = pack.packCount * pack.unitQty;
    if (!(totalQty > 0)) return { ...out, reason: "bad_pack_quantity" };
    const read = { ...out, read: true, kind, totalQty, unitToken: pack.unitRaw };

    if (kind === "count") {
      // A stated count IS the piece count, in eaches ("15 doz" → 180).
      read.pieceCount = round6(totalQty * (pack.unitNorm.factor ?? 1));
      read.pieceCountSource = "stated_count";
      read.measures = [countMeasure(read.pieceCount, "explicit_pack_text")];
      return read;
    }

    // Mass/volume with an explicit leading count: "20/12 OZ" is twenty pieces
    // of twelve ounces. The count is explicit in the text; the total is
    // derived arithmetic. An implied packCount of 1 ("50 LB") states no count.
    // When the text NAMES the counted containers ("24 × 6.8 oz bottles",
    // "2 bags × 5 lb") the count measure carries that noun, so a bottle count
    // can satisfy a bottle-counted shelf and never a can-counted one. A
    // "per"-wrapper is the package itself, never what the leading N counts.
    if (pack.packCountExplicit && pack.packCount >= 1) {
      const countedContainer = out.containerWord && out.containerPosition !== "per" ? out.containerWord : null;
      read.pieceCount = pack.packCount;
      read.pieceCountSource = "inner_pack";
      read.perPiece = {
        qty: pack.unitQty,
        unit: pack.unitRaw,
        min: pack.unitQtyMin ?? null,
        max: pack.unitQtyMax ?? null,
        approximate: pack.approximate === true,
      };
      read.measures = [
        countMeasure(pack.packCount, "explicit_pack_text", countedContainer),
        {
          kind, totalQty, unitToken: pack.unitRaw,
          approximate: pack.approximate === true,
          source: "deterministic_derived",
        },
      ];
    } else {
      read.measures = [{
        kind, totalQty, unitToken: pack.unitRaw,
        approximate: pack.approximate === true,
        source: "explicit_pack_text",
      }];
    }
    return read;
  }

  // A stated count of pieces is a measurement, not a subjective container.
  const piece = readPieceNoun(pack.unitRaw ?? text);
  if (!piece) return { ...out, reason: "subjective_container" };
  const totalQty = pack.packCount * pack.unitQty;
  if (!(totalQty > 0)) return { ...out, reason: "bad_pack_quantity" };
  return {
    ...out,
    read: true,
    kind: "count",
    totalQty,
    unitToken: "each",
    unitNorm: { key: "each", kind: "count", factor: 1, base: "each" },
    pieceWord: piece.word,
    confidence: piece.confidence,
    pieceCount: round6(totalQty),
    pieceCountSource: "piece_noun",
    measures: [countMeasure(round6(totalQty), "explicit_pack_text", piece.word)],
  };
}

const round6 = (n) => Math.round(n * 1e6) / 1e6;

const countMeasure = (qty, source, pieceNoun = null) => ({
  kind: "count", totalQty: qty, unitToken: "each", approximate: false, source,
  // The specific container/piece the count counts, when the text names one
  // ("24 × 6.8 oz bottles" counts BOTTLES). Null for anonymous counts.
  ...(pieceNoun ? { pieceNoun } : {}),
});

/**
 * "40 LB / 140 CT" — a weight AND a count of the same package. Both sides must
 * parse on their own as single measures ("20/12 OZ" does not split: its left
 * side is a bare number), and the kinds must be count vs mass/volume.
 */
function readDualMeasure(text) {
  const segments = String(text).split(/\s*\/\s*/);
  if (segments.length !== 2) return null;
  const sides = segments.map((seg) => {
    const trimmed = seg.trim();
    if (!trimmed || !/\d/.test(trimmed)) return null;
    const parsed = parsePackSize(trimmed);
    if (!parsed.parsed || parsed.special) return null;
    const kind = parsed.unitNorm?.kind;
    if (kind !== "mass" && kind !== "volume" && kind !== "count") return null;
    const totalQty = parsed.packCount * parsed.unitQty;
    if (!(totalQty > 0)) return null;
    return { parsed, kind, totalQty };
  });
  if (sides.some((s) => s == null)) return null;

  const countSide = sides.find((s) => s.kind === "count");
  const sizeSide = sides.find((s) => s.kind === "mass" || s.kind === "volume");
  if (!countSide || !sizeSide) return null;

  const pieceCount = round6(countSide.totalQty * (countSide.parsed.unitNorm.factor ?? 1));
  const perPieceQty = pieceCount > 0 ? round6((sizeSide.totalQty * 1) / pieceCount) : null;
  return {
    read: true,
    reason: null,
    kind: sizeSide.kind,
    packCount: 1,
    unitQty: sizeSide.totalQty,
    totalQty: sizeSide.totalQty,
    unitRaw: sizeSide.parsed.unitRaw,
    unitToken: sizeSide.parsed.unitRaw,
    unitNorm: sizeSide.parsed.unitNorm,
    confidence: Math.min(sizeSide.parsed.confidence ?? 0.8, countSide.parsed.confidence ?? 0.8),
    approximate: sizeSide.parsed.approximate === true,
    pieceCount,
    pieceCountSource: "dual_measure",
    perPiece: perPieceQty != null
      ? {
        qty: perPieceQty, unit: sizeSide.parsed.unitRaw, min: null, max: null,
        approximate: true, derived: true,
      }
      : null,
    measures: [
      countMeasure(pieceCount, "explicit_pack_text"),
      {
        kind: sizeSide.kind,
        totalQty: sizeSide.totalQty,
        unitToken: sizeSide.parsed.unitRaw,
        approximate: sizeSide.parsed.approximate === true,
        source: "explicit_pack_text",
      },
    ],
  };
}

/**
 * The word the pack itself uses for ONE orderable package, or null.
 *
 * `purchase_unit` is supposed to name the thing the vendor accepts as one
 * order unit, and it is load-bearing: `packageToCountUnitQty` branches on it
 * (container identity, piece identity, direct conversion), `interpretPackage`
 * re-runs that live, and `packageBasisFingerprint` keys stored comparisons on
 * it. Getting the word wrong is not a labelling slip — it changes arithmetic,
 * and it tells a vendor to ship the wrong object.
 *
 * The import ledger calls the price of one stated package `case_price`,
 * because that is the field a bare "Price" column lands in when the row also
 * names its purchase form. Reading that FIELD NAME back as a package NOUN is
 * how "750 mL bottle — $22.95" became `purchase_unit: 'case'`: an order for
 * three bottles printed as an order for three cases. The pack description was
 * sitting beside it, saying "bottle", and nothing looked.
 *
 * The reading is deliberately narrow. A pack names its own order unit only
 * when it describes exactly ONE container:
 *
 *   "750 mL bottle"      → bottle    (one bottle, and that is what is bought)
 *   "25 lb sack"         → sack
 *   "5 gal jug"          → jug
 *   "12 ct box"          → box
 *   "12 × 750 mL bottle" → null      (twelve bottles come in something the
 *                                     pack does not name — do not guess "case",
 *                                     and do not say "bottle")
 *   "24/12 OZ"           → null      (no container word at all)
 *   "case" / "1 case"    → null      (a wrapper with no contents stated is the
 *                                     subjective-container case; it carries no
 *                                     more information than the caller has)
 *
 * A PIECE noun is never an order unit: "6 × 1.5 lb slices" describes what is
 * inside, not what the vendor sells.
 *
 * @param {string} packDescription
 * @returns {string|null} the canonical container word, or null when the pack
 *                        does not state one order unit
 */
export function statedOrderUnitFromPack(packDescription) {
  const pack = readPack(packDescription);
  if (!pack.containerWord) return null;
  // A stated count above one means the container is an INNER pack; the order
  // unit is the wrapper around them, which this text does not name.
  if (pack.packCount != null && pack.packCount > 1) return null;
  // The subjective-container shapes ("case", "6/#10") state a wrapper with no
  // contents; they add nothing the caller did not already know.
  if (!pack.read) return null;
  const word = canonicalPackWord(pack.containerWord);
  if (!word || !isContainerWord(word) || PIECE_WORDS.has(word)) return null;
  return word;
}
